import React, { useEffect, useState } from 'react';
import { Images } from "../utils/imageConstants";
import type { IPlayerByLeague } from '../views/leaguesHome/squads/types/squads';
import type { ITeamsSquad } from '../views/leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d';
import { useLocation } from 'react-router-dom';
import { formatPlayerRoleLabel, resolvePlayerPortraitUrl } from '../helper/helper';


interface PlayerCardProps {
  player: ITeamsSquad | IPlayerByLeague;
  /** When set, the card is keyboard-focusable and activates on click / Enter / Space */
  onClick?: () => void;
}

function isWicketKeeperRole(roleKey: string): boolean {
  if (!roleKey) return false;
  if (roleKey === "WICKET_KEEPER" || roleKey === "WICKETKEEPER") return true;
  return roleKey.includes("WICKET") && roleKey.includes("KEEP");
}

export const PlayerCard: React.FC<PlayerCardProps> = ({
  player,
  onClick,
}) => {
  const location = useLocation();
  const leagueHome = location.pathname.includes("/leagues-home");
  const roleKey = (player?.playerRole ?? "").trim().toUpperCase();

  const roleDisplay = player?.playerRole?.trim()
    ? formatPlayerRoleLabel(player.playerRole)
    : "NA";

  const normalizedBattingStyle = (player?.battingStyle ?? "").trim().toUpperCase();
  const normalizedBowlingStyle = (player?.bowlingStyle ?? "").trim().toUpperCase();
  const hasBatSkill = Boolean(normalizedBattingStyle && normalizedBattingStyle !== "NULL");
  const hasBowlSkill = Boolean(normalizedBowlingStyle && normalizedBowlingStyle !== "NULL");
  const isBowlerRole = roleKey.includes("BOWL");
  const isBatterRole = roleKey.includes("BAT");

  const isCaptainFromRole =
    roleKey === "CAPTAIN" ||
    roleKey === "TEAM_CAPTAIN" ||
    (player as { captain?: boolean | null }).captain === true;
  const isViceCaptainFromRole =
    roleKey === "VICE_CAPTAIN" ||
    roleKey === "VICECAPTAIN" ||
    roleKey.includes("VICE") ||
    (player as { viceCaptain?: boolean | null }).viceCaptain === true;
  const isWkFromRole =
    isWicketKeeperRole(roleKey) ||
    (player as { wicketKeeper?: boolean | null }).wicketKeeper === true;

  const isCaptain = isCaptainFromRole;
  const isViceCaptain = isViceCaptainFromRole;
  const isWk = isWkFromRole;

  const showBatIcon = hasBatSkill || isBatterRole || roleKey.includes("ALL_ROUNDER");
  const showBallIcon = hasBowlSkill || isBowlerRole || roleKey.includes("ALL_ROUNDER");

  const resolvedPortrait = resolvePlayerPortraitUrl(player);
  const [photoFailed, setPhotoFailed] = useState(false);

  useEffect(() => {
    setPhotoFailed(false);
  }, [player.playerId, resolvedPortrait]);

  const photoSrc =
    resolvedPortrait && !photoFailed
      ? resolvedPortrait
      : Images.team_member_placeholder;

  const playerName = `${player?.playerFirstName ?? ""} ${player?.playerLastName ?? ""}`.trim() || "Player";

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`ti-player${onClick ? " ti-player--clickable" : ""}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? `View profile: ${playerName}` : undefined}
    >
      <div className="ti-hero">
        {isCaptain && (
          <span className="ti-badge ti-badge--captain" aria-label="Captain">
            <img src={Images.c_icon} alt="" />
          </span>
        )}
        {isViceCaptain && (
          <span className="ti-badge ti-badge--vice" aria-label="Vice-captain">
            <img src={Images.vc_icon} alt="" />
          </span>
        )}

        <div className="ti-hero-corner-right" aria-hidden>
          {showBallIcon && <img src={Images.ball_icon} alt="" />}
          {showBatIcon && <img src={Images.bat_icon} alt="" />}
          {isWk && (
            <span className="ti-wk-badge" title="Wicket keeper">
              <img src={Images.hands_icon} alt="" />
            </span>
          )}
        </div>

        <img
          className="ti-photo"
          src={photoSrc}
          alt={playerName}
          onError={() => {
            if (resolvedPortrait) setPhotoFailed(true);
          }}
        />
      </div>
      <div className="ti-divider" />
      <div className="ti-meta">
        <div className="ti-name">{playerName}</div>
        {!leagueHome && <div className="ti-role">{roleDisplay}</div>}
      </div>
    </div>
  );
};
